// Comfort-language preference: picks which Indian language the AI mixes with
// English when replying. Written in Latin script so the TTS keeps working.

// `group`: 'base' = plain English, 'mix' = Latin-script bilingual mixes,
// 'native' = pure native-script replies. Used to render section headers.
export const COMFORT_LANGUAGES = [
  { code: 'en',    group: 'base',   label: 'English',             short: 'EN' },

  { code: 'en-hi', group: 'mix',    label: 'English + Hindi',     short: 'Hinglish' },
  { code: 'en-te', group: 'mix',    label: 'English + Telugu',    short: 'Tenglish' },
  { code: 'en-ta', group: 'mix',    label: 'English + Tamil',     short: 'Tanglish' },
  { code: 'en-kn', group: 'mix',    label: 'English + Kannada',   short: 'Kanglish' },
  { code: 'en-ml', group: 'mix',    label: 'English + Malayalam', short: 'Manglish' },
  { code: 'en-bn', group: 'mix',    label: 'English + Bengali',   short: 'Benglish' },
  { code: 'en-gu', group: 'mix',    label: 'English + Gujarati',  short: 'Gujlish'  },
  { code: 'en-mr', group: 'mix',    label: 'English + Marathi',   short: 'Minglish' },
  { code: 'en-pa', group: 'mix',    label: 'English + Punjabi',   short: 'Punglish' },
  { code: 'en-or', group: 'mix',    label: 'English + Odia',      short: 'Odlish'   },

  { code: 'hi',    group: 'native', label: 'हिन्दी (Hindi only)',    short: 'हिन्दी' },
  { code: 'te',    group: 'native', label: 'తెలుగు (Telugu only)',    short: 'తెలుగు' },
  { code: 'ta',    group: 'native', label: 'தமிழ் (Tamil only)',      short: 'தமிழ்' },
  { code: 'kn',    group: 'native', label: 'ಕನ್ನಡ (Kannada only)',    short: 'ಕನ್ನಡ' },
  { code: 'ml',    group: 'native', label: 'മലയാളം (Malayalam only)', short: 'മലയാളം' },
  { code: 'bn',    group: 'native', label: 'বাংলা (Bengali only)',    short: 'বাংলা' },
  { code: 'gu',    group: 'native', label: 'ગુજરાતી (Gujarati only)', short: 'ગુજરાતી' },
  { code: 'mr',    group: 'native', label: 'मराठी (Marathi only)',    short: 'मराठी' },
  { code: 'pa',    group: 'native', label: 'ਪੰਜਾਬੀ (Punjabi only)',   short: 'ਪੰਜਾਬੀ' },
  { code: 'or',    group: 'native', label: 'ଓଡ଼ିଆ (Odia only)',       short: 'ଓଡ଼ିଆ' },
];

const STORAGE_KEY = 'memorycoach:comfortLanguage';

export function getComfortLanguage() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && COMFORT_LANGUAGES.some(l => l.code === v)) return v;
  } catch (_) {}
  return 'en';
}

export function setComfortLanguage(code) {
  try { localStorage.setItem(STORAGE_KEY, code); } catch (_) {}
}

// Indian states & UTs — picked for localized food/place/festival/cricketer
// examples in the memory hooks. Order kept alphabetical.
export const INDIAN_STATES = [
  { code: 'auto', label: 'Auto / Any' },
  { code: 'ap',   label: 'Andhra Pradesh' },
  { code: 'ar',   label: 'Arunachal Pradesh' },
  { code: 'as',   label: 'Assam' },
  { code: 'br',   label: 'Bihar' },
  { code: 'cg',   label: 'Chhattisgarh' },
  { code: 'ga',   label: 'Goa' },
  { code: 'gj',   label: 'Gujarat' },
  { code: 'hr',   label: 'Haryana' },
  { code: 'hp',   label: 'Himachal Pradesh' },
  { code: 'jh',   label: 'Jharkhand' },
  { code: 'ka',   label: 'Karnataka' },
  { code: 'kl',   label: 'Kerala' },
  { code: 'mp',   label: 'Madhya Pradesh' },
  { code: 'mh',   label: 'Maharashtra' },
  { code: 'mn',   label: 'Manipur' },
  { code: 'ml',   label: 'Meghalaya' },
  { code: 'mz',   label: 'Mizoram' },
  { code: 'nl',   label: 'Nagaland' },
  { code: 'od',   label: 'Odisha' },
  { code: 'pb',   label: 'Punjab' },
  { code: 'rj',   label: 'Rajasthan' },
  { code: 'sk',   label: 'Sikkim' },
  { code: 'tn',   label: 'Tamil Nadu' },
  { code: 'ts',   label: 'Telangana' },
  { code: 'tr',   label: 'Tripura' },
  { code: 'up',   label: 'Uttar Pradesh' },
  { code: 'uk',   label: 'Uttarakhand' },
  { code: 'wb',   label: 'West Bengal' },
  { code: 'dl',   label: 'Delhi' },
  { code: 'jk',   label: 'Jammu & Kashmir' },
  { code: 'la',   label: 'Ladakh' },
  { code: 'py',   label: 'Puducherry' },
  { code: 'ch',   label: 'Chandigarh' },
  { code: 'an',   label: 'Andaman & Nicobar' },
];

const STATE_KEY = 'memorycoach:comfortState';

export function getComfortState() {
  try {
    const v = localStorage.getItem(STATE_KEY);
    if (v && INDIAN_STATES.some(s => s.code === v)) return v;
  } catch (_) {}
  return 'auto';
}

export function setComfortState(code) {
  try { localStorage.setItem(STATE_KEY, code); } catch (_) {}
}

// Detect which Indian script (if any) dominates a piece of text, so we can
// auto-switch when a student types in their mother tongue. Returns a comfort-
// language code or null when nothing matches.
export function detectComfortLanguageFromText(text) {
  if (!text || text.length < 3) return null;
  const scripts = [
    { code: 'en-hi', re: /[ऀ-ॿ]/g }, // Devanagari (Hindi/Marathi share this; we pick Hindi as default — user can switch)
    { code: 'en-te', re: /[ఀ-౿]/g }, // Telugu
    { code: 'en-ta', re: /[஀-௿]/g }, // Tamil
    { code: 'en-kn', re: /[ಀ-೿]/g }, // Kannada
    { code: 'en-ml', re: /[ഀ-ൿ]/g }, // Malayalam
    { code: 'en-bn', re: /[ঀ-৿]/g }, // Bengali
    { code: 'en-gu', re: /[઀-૿]/g }, // Gujarati
    { code: 'en-pa', re: /[਀-੿]/g }, // Gurmukhi (Punjabi)
    { code: 'en-or', re: /[଀-୿]/g }, // Odia
  ];
  let best = null;
  let bestCount = 0;
  for (const s of scripts) {
    const count = (text.match(s.re) || []).length;
    if (count > bestCount) { bestCount = count; best = s.code; }
  }
  // Need at least a few native characters to be confident.
  return bestCount >= 3 ? best : null;
}
